import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import {
  DatePickerLocalization,
  DatePickerSchemeDefinition,
  DatePickerSchemeName,
  Day,
  SelectedDatePart,
} from './constants/date-picker-types';
import { getMonthName, getToday, getWeekDays } from '../../utils/date-helper';
import {
  FormComponent,
  handleCustomValidationDelegate,
  validateDelegate,
  validateRequiredValueDelegate,
} from '../../common/form-component';
import { addDays, format, isAfter, isBefore, isWeekend, parse, subDays } from 'date-fns';
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from '../../common/localizable-component';
import { KeyIds } from '../../constants/keys';
import pfx from '../../utils/style-class-prefix-helper';
import { DATE_PICKER_LOCALIZATION } from './constants/date-picker-i18n';
import { ICON_ICON_SCHEME } from '../../constants/global-constants';
import Fragment from '../../common/fragment';
import { DatePickerSchemeConfiguration } from '../../constants/colors';
import { DATE_PICKER_SCHEME_CONFIG, DATE_PICKER_SCHEME_MAP } from './constants/date-picker-constants';
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from '../../common/colorable-component';
import { isMobile } from '../../utils/screen-helper';

import { BackgroundColor } from '../background/constants/background-types';
import { StyledComponent } from '../../decorators/styled-component';
import { FormFieldNecessity } from '../form/constants/form-types';
import { FORM_FIELD_NECESSITY_NONE } from '../form/constants/form-constants';
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 */
@Component({
  tag: 'envc-eon-ui-date-picker',
  styleUrl: 'date-picker.scss',
  shadow: true,
  formAssociated: true,
})
export class DatePicker
  implements
    ColorableComponent<DatePickerSchemeConfiguration<DatePickerSchemeDefinition>>,
    FormComponent<any, DatePickerLocalization>,
    LocalizableComponent<DatePickerLocalization>
{
  // @InitLogging() sentryTransaction;
  @AttachInternals() internals: ElementInternals;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEnvcEonUiDatePickerElement;

  @State() datePickerVisible: boolean = false;
  @State() year: number;
  @State() month: number;
  @State() focussedDay: number;
  @State() focussedMonth: number;
  @State() focussedYear: number;
  @State() selectedDay: number;
  @State() calendarDays: Array<Day>;
  @State() todayTimeStamp: number;
  @State() selectedDateValue: string;
  @State() minTimestamp: number;
  @State() maxTimestamp: number;
  @State() isInfoIconAvailable: boolean;
  @State() disabledDatesArr: Array<string> = [];
  @State() isHovered: boolean = false;
  @State() isMobileDevice: boolean;

  schemeConfig: DatePickerSchemeConfiguration<DatePickerSchemeDefinition> = DATE_PICKER_SCHEME_CONFIG;
  schemeMap = DATE_PICKER_SCHEME_MAP;

  localization: Localization<DatePickerLocalization> = DATE_PICKER_LOCALIZATION;
  currentLocalization: DatePickerLocalization;

  private calendarContainer?: HTMLDivElement;
  private calendarInput?: HTMLEnvcEonUiInputElement;
  private calendarInputElement?: HTMLInputElement;
  private infoIconComponent: HTMLEnvcEonUiInfoIconElement;
  private relevantYears: Array<number> = [];
  private yearSelectionContainer: HTMLElement;
  private monthSelectionContainer: HTMLElement;

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: DatePickerSchemeName;

  @State() activeScheme: DatePickerSchemeDefinition;

  /**
   * **Internal use only!**
   * &nbsp;<br>
   * Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) isFormComponent: boolean = true;

  /**
   * the name of the datepicker, must be unique per page!
   * @example <eon-ui-date-picker name="date"></eon-ui-date-picker>
   */
  @Prop() name: string = '';

  /**
   * **DEPRECATED - use value attribute instead**
   * &nbsp;<br>
   * Set the selected date of the date-picker in format "dd.mm.yyyy".
   * @example <eon-ui-date-picker selected-date="22.03.2022"></eon-ui-date-picker>
   */
  @Prop({ reflect: true, mutable: true }) selectedDate: string;

  /**
   * The value of the Date Picker in default format (yyyy-MM-dd). Can also be used to set the date picker to a desired date.
   * @example <eon-ui-date-picker value="2022-03-22"></eon-ui-date-picker>
   */
  @Prop({ reflect: true, mutable: true }) value: string;

  /**
   * Property  to store the formatted value
   */
  @Prop({ reflect: true, mutable: true }) formattedValue: string = '';

  /**
   * Set to true if the input is required.
   *
   * @example <eon-ui-date-picker  required="required"></eon-ui-date-picker >
   */
  @Prop() required: boolean;

  /**
   * Set to true if the input is disabled.
   *
   * @example <eon-ui-date-picker  disabled="disabled"></eon-ui-date-picker >
   */
  @Prop() disabled: boolean;

  /**
   * Sets the label for the input element.
   *
   * @example <eon-ui-date-picker  label="First Name"></eon-ui-date-picker >
   */
  @Prop() label: string;

  /**
   * Defines whether the label should be placed above the component.
   */
  @Prop() labelOutside: boolean = false;

  /**
   * Define the size of the input element
   */
  @Prop() size: 'small' | 'normal' = 'normal';

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Sets the text of the error message.
   * @example <eon-ui-date-picker  error="true" error-message="this is an error message"></eon-ui-date-picker >
   */
  @Prop({ mutable: true }) errorMessage: string;

  /**
   * An empty Placeholder for an optional custom Error Message for CustomValidation.
   * @example <eon-ui-checkbox custom-error-message="this is an custom error message"></eon-ui-checkbox>
   */
  @Prop({ mutable: true }) customErrorMessage: string;

  /**
   * A custom message to show when the required field has no value
   */
  @Prop() customRequiredMessage: string;

  /**
   * **Internal use only! Please don't use this property directly.**
   * &nbsp;<br>
   * Set to true if the Input is in error state and the error message should be displayed.
   * @example <eon-ui-date-picker invalid="true"></eon-ui-date-picker >
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean = false;

  /**
   * ** A dedicated invalid attribute to be used in more reactive scenarios like angular, where the framework controls validation rules.**
   * &nbsp;<br>
   * @example <envc-eon-ui-input ing-invalid="true"></envc-eon-ui-input>
   */
  @Prop() showError: boolean | undefined;

  /**
   * Sets the text of the success message.
   * @example <eon-ui-date-picker success="true" success-message="this is a success message"></eon-ui-date-picker >
   */
  @Prop() successMessage: string;

  /**
   * Set to true if the Input is in success state and the success message should be displayed.
   * @example <eon-ui-date-picker success="true"></eon-ui-date-picker >
   */
  @Prop() success: boolean = false;

  /**
   * Set to true if the Input is in info state and the info message should be displayed.
   * @example <eon-ui-date-picker info="true"></eon-ui-date-picker >
   */
  @Prop() info: boolean = false;

  /**
   * Sets the text of the info message.
   * @example <eon-ui-date-picker info="true" info-message="this is an info message"></eon-ui-date-picker >
   */
  @Prop() infoMessage: string;

  /**
   * Determine if the todays date is selected automatically
   * @example <eon-ui-date-picker select-today="true"></eon-ui-date-picker>
   */
  @Prop() selectToday: boolean;

  /**
   * The format of the date used for the date picker. Use this for different separators too."
   * If no format is provided, the format of the current language is taken
   * dd = days
   * MM = months (case sensitive!!)
   * yyyy = years
   * @example <eon-ui-date-picker date-format="dd/MM/yyyy"></eon-ui-date-picker>
   */
  @Prop({ mutable: true }) dateFormat: string;

  /**
   * Determine if the weekends are available
   * @default true
   * @example <eon-ui-date-picker weekends="false"></eon-ui-date-picker>
   */
  @Prop() weekends: boolean = true;

  /**
   * Determine if the sunday is available
   * @default true
   * @example <eon-ui-date-picker sunday="false"></eon-ui-date-picker>
   */
  @Prop() sunday: boolean = true;

  /**
   * The minimum date. provided in format "yyyy-MM-dd"
   * @example <eon-ui-date-picker min="2020-05-24"></eon-ui-date-picker>
   */
  @Prop({ mutable: true, reflect: true }) min: string = '1950-01-01';

  /**
   * The maximum date. provided in format "yyyy-MM-dd"
   * @example <eon-ui-date-picker min="2020-05-24"></eon-ui-date-picker>
   */
  @Prop({ mutable: true, reflect: true }) max: string = '2100-12-24';

  /**
   * Exclude specific dates from the calendar selection. Format: "yyyy-MM-dd, yyyy-MM-dd, ..."
   */
  @Prop() disableDates: string;

  /**
   * The number of allowed days before the current date
   */
  @Prop() maxDaysBefore: number;

  /**
   * The number of allowed days after the current date
   */
  @Prop() maxDaysAfter: number;

  /**
   * Determine wether the calendar should be closed when a date is selected or not
   * @example <eon-ui-date-picker close-on-select="true"></eon-ui-date-picker>
   */
  @Prop() closeOnSelect: boolean = true;

  /**
   * Determine wether the calendar should open or not when the input is focused
   * @example <eon-ui-date-picker hide-popup="true"></eon-ui-date-picker>
   */
  @Prop() hidePopup: boolean = false;

  /**
   * Error message which gets displayed when the date-range is invalid
   */
  @Prop() dateRangeErrorMessage: string;

  /**
   * Determine if the trailing icon to clear the input is shown.
   * @example <eon-ui-date-picker clear-input-icon="true"></eon-ui-date-picker>
   */
  @Prop() clearInputIcon: boolean = true;

  /**
   * Whether the input field should have a max width or auto width.
   */
  @Prop() autoInputWidth: boolean = false;

  /**
   * Represents the necessity level for the input validation.
   * Possible values:
   * - 'none' : No validation required.
   * - 'optional' : Validation is optional.
   * - 'required' : Validation is mandatory.
   */
  @Prop() highlightedNecessity: FormFieldNecessity = FORM_FIELD_NECESSITY_NONE;

  /**
   * Specifies the label to use for accessibility.
   */
  @Prop({ attribute: 'a11y-aria-label', mutable: true }) a11yAriaLabel: string;
  /**
   * State for visibility handling of month and year selection
   */
  @State() monthYearSelection: '' | 'month' | 'year' = '';

  /**
   * State for which part of the date is currently selected
   */
  @State() selectedDatePart: SelectedDatePart = '';

  /**
   * Decides if the date picker container should be on the right.
   */
  @State() datePickerContainerPositionRight: boolean = false;

  /**
   * Will emit when the selected date has changed. Will emit the selected javascript date.
   */
  @Event() valueChanged: EventEmitter<string>;

  /**
   * Raised when the date picker has been loaded.
   */
  @Event() datePickerLoad: EventEmitter<string>;

  /**
   * Raised when the popup has been opened.
   */
  @Event() datePickerOpened: EventEmitter<string>;

  /**
   * Raised when the popup has been closed.
   */
  @Event() datePickerClosed: EventEmitter<string>;

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param component The FormComponent that should handle the custom validation.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    // ENVIAM
    const requiredValid = await validateRequiredValueDelegate(this);
    this.valueMissing = !requiredValid;
    return requiredValid;
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch('required')
  @Method()
  async validate(): Promise<boolean> {
    const valid = await validateDelegate(this);
    // ENVIAM
    this.updateInternalValidity();
    return valid;
  }

  /**
   * Removes the value and puts the input into initial state with an empty value showing the default date mask.
   */
  @Method()
  async purgeValue() {
    this.calendarInput.purgeValue();
  }

  /**
   * Update custom-required-message
   */
  @Watch('customRequiredMessage')
  setCustomRequiredMessage() {
    this.validate();
  }

  @Listen('click', { target: 'document' })
  onDocumentClick(event: any) {
    this.closeDatepickerEvent(event);
  }

  @Listen('resize', { target: 'window' })
  onViewportSizeChange() {
    this.isMobileDevice = isMobile();
    this.positionDatePickerContainer();
  }

  /**
   * Determines the position of the date picker container relative to the viewport
   * based on the location of the parent element that triggers the event.
   *
   * - If the parent's left position is greater than or equal to 55% of the viewport width,
   *   the date picker will open on the right.
   * - Otherwise, the date picker will open on the left.
   *
   * @private
   */
  private positionDatePickerContainer() {
    const parentElement = this.hostElement.parentElement;

    // Detects the position of parent
    const leftPos = parentElement?.getBoundingClientRect().left;

    // Determines whether the date picker should open on the right or left
    this.datePickerContainerPositionRight = window.innerWidth / 1.8 <= leftPos;
  }

  componentWillLoad() {
    this.applyScheme();
    this.transformDateFormatIfNecessary();
    this.setCurrentLocalization();
    this.setDateFormat();
    const date = getToday();
    this.year = date.getFullYear();
    this.month = date.getMonth();
    const dayOfMonth = date.getDate();
    this.getDaysForCalendar();
    this.todayTimeStamp = new Date(this.year, this.month, dayOfMonth).getTime();
    this.getMaxDaysAfter();
    this.getMaxDaysBefore();
    this.getMaxTimestamp();
    this.getMinTimestamp();
    this.splitDisabledDates();
    this.getRelevantYears();
    this.positionDatePickerContainer();

    document.addEventListener('keydown', (e) => {
      this.closeDatePickerKeyboardEvent(e);
    });

    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
    this.infoIconComponent = this.hostElement.querySelector('envc-eon-ui-info-icon');
    !!this.infoIconComponent && this.initInfoIconEventListenerForCollapsing();
    this.observeViewportVisibility(this.hostElement);
  }

  componentDidLoad() {
    this.setInitialDate();
    this.setInputValueAttributeObserver();
    this.setCssVariables();
    this.setCustomErrorMessage();
    this.calendarInputElement = this.calendarInput.shadowRoot?.querySelector('input');

    // If a solution uses deprecated approach of selected-date attribute it is transformed to this.value here.
    if (this.selectedDate?.match(/\d\d[.]\d\d[.]\d\d\d\d/)) {
      this.value = this.transformToDefaultDateFormat(this.selectedDate, 'dd.MM.yyyy');
    }

    this.datePickerLoad.emit();
    // ENVIAM
    this.updateInternalState();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', (e) => {
      this.closeDatePickerKeyboardEvent(e);
    });
  }

  @Watch('scheme')
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch('activeScheme')
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  initInfoIconEventListenerForCollapsing() {
    this.infoIconComponent.addEventListener('infoIconClick', () => {
      this.closeCalendar();
    });
  }

  /**
   * Validates that the given date is in the min and max range
   */
  validateMinMax(): string {
    const minDate = parse(this.min, 'yyyy-MM-dd', new Date());
    const maxDate = parse(this.max, 'yyyy-MM-dd', new Date());
    const selectedDate = new Date(this.selectedDay);

    if (isBefore(selectedDate, minDate) || isAfter(selectedDate, maxDate)) {
      const dateRangeErrorMessage = this.dateRangeErrorMessage
        ? this.dateRangeErrorMessage
        : this.currentLocalization.dateRangeErrorMessage;

      return dateRangeErrorMessage;
    }

    return '';
  }

  /**
   * Validates if the entered date is a weekend or not
   */
  validateWeekend(): string {
    const selectedDate = new Date(this.selectedDay);
    if (!this.weekends && isWeekend(selectedDate)) {
      return this.currentLocalization.weekendErrorMessage;
    }

    return '';
  }

  validateDisableDates(): string {
    const date = new Date(this.selectedDay);
    try {
      const formattedValue = format(date, 'yyyy-MM-dd');
      if (this.disabledDatesArr.includes(formattedValue)) {
        return this.currentLocalization.disableDatesErrorMessage;
      }
    } catch (error) {}
    return '';
  }

  @Watch('datePickerVisible')
  calculateDatePickerWrapperHeight() {
    this.observeViewportVisibility(this.hostElement);
  }

  observeViewportVisibility(element: HTMLElement) {
    const documentHeight = document.documentElement.scrollHeight;
    const datePickerTop = this.hostElement.getBoundingClientRect()?.top;
    const distanceFromBottom = documentHeight - (datePickerTop + window.scrollY);
    if (window && window.IntersectionObserver) {
      const datePickerObserver = new window.IntersectionObserver((entries: IntersectionObserverEntry[]) => {
        entries.forEach((entry) => {
          if (entry.intersectionRatio > 0) {
            if (this.calendarInput && this.calendarContainer) {
              const datePickerWrapper = this.calendarInput.parentElement;
              const calenderInputHeight = this.calendarInput?.getBoundingClientRect().height;
              const calenderContainerHeight = this.calendarContainer?.getBoundingClientRect().height;
              const totalDatePickerHeight = calenderInputHeight + calenderContainerHeight;
              if (this.datePickerVisible && distanceFromBottom < totalDatePickerHeight) {
                datePickerWrapper.style.minHeight = `${calenderInputHeight + calenderContainerHeight}px`;
              } else {
                datePickerWrapper.removeAttribute('style');
              }
            }
          }
        });
      });
      datePickerObserver.observe(element);
    }
  }

  setInputValueAttributeObserver() {
    var inputValueAttributeObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          if (this.value !== this.calendarInput.value) {
            this.value = this.calendarInput.value;
          }
          if (this.formattedValue !== this.calendarInput.formattedValue) {
            this.formattedValue = this.calendarInput.formattedValue;
          }
        }
      });
    });

    inputValueAttributeObserver.observe(this.calendarInput, {
      attributes: true, //configure it to listen to attribute changes
    });
  }

  transformToDefaultDateFormat(dateString: string, format: string): string {
    let defaultFormatString = '';

    switch (format) {
      case 'dd.MM.yyyy':
        const dateStringArray = dateString.split('.');
        defaultFormatString = `${dateStringArray[2]}-${dateStringArray[1]}-${dateStringArray[0]}`;
        break;
    }

    return defaultFormatString;
  }

  @Watch('selectToday')
  @Watch('value')
  setInitialDate() {
    if (this.selectToday) {
      this.selectedDay = this.value ? parse(this.value, 'yyyy-MM-dd', new Date())?.getTime() : this.todayTimeStamp;
    }

    if (this.value) {
      this.selectedDay = parse(this.value, 'yyyy-MM-dd', new Date())?.getTime();
    } else {
      const date = getToday();
      this.year = date.getFullYear();
      this.month = date.getMonth();
    }
  }

  @Watch('value')
  emitValueChange() {
    this.valueChanged.emit(this.value);
  }

  closeDatePickerKeyboardEvent(_e: KeyboardEvent) {
    if (_e.key === KeyIds.ESCAPE) {
      this.closeCalendar();
    }
  }

  closeDatepickerEvent(e) {
    const srcElement = e.srcElement as HTMLElement;
    let hostElementIsPartOfParentShadowRoot = e.composedPath().includes(this.hostElement);

    if (
      srcElement !== this.hostElement &&
      !hostElementIsPartOfParentShadowRoot &&
      !this.hostElement.contains(srcElement)
    ) {
      //in edge the tagName is not "EON-UI-DATE-PICKER" so we have to check if the hostelement contains the clicked element
      this.closeCalendar();
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  setDateFormat() {
    if (!this.dateFormat) {
      this.dateFormat = this.currentLocalization.dateFormat;
    }
  }

  private openCalendar() {
    if (this.calendarContainer) this.calendarContainer.classList.add(pfx('open-calendar').toString());
    this.datePickerVisible = true;

    if (this.calendarContainer.clientWidth > 350) {
      this.toggleMonthYearSelection('month');
      // focus current month
      setTimeout(() => {
        const currentMonth = this.value
          ? (this.monthSelectionContainer.querySelector('.eonui-selected') as HTMLButtonElement)
          : (this.monthSelectionContainer.querySelector('.eonui-today') as HTMLButtonElement);
        currentMonth.focus();
      }, 200);
    } else {
      // focus month selector button
      (this.calendarContainer.querySelector('.eonui-month') as HTMLButtonElement).focus();
    }
  }

  private toggleOpen(_event: CustomEvent<MouseEvent>) {
    if (this.datePickerVisible) {
      this.closeCalendar();
    } else {
      this.openCalendar();
    }

  }

  private closeCalendar(selectDate = false) {
    this.datePickerVisible = false;
    if (this.calendarContainer?.classList.contains(pfx('open-calendar').toString())) {
      this.calendarContainer.classList.remove(pfx('open-calendar').toString());
      this.calendarInputElement.focus();
      this.monthYearSelection = '';
      if (selectDate) {
        setTimeout(() => {
          this.calendarInputElement.select();
        }, 200);
      }
    }
  }

  @Watch('datePickerVisible')
  datePickerVisibilityEventHandler() {
    if (this.datePickerVisible) {
      this.datePickerOpened.emit();
    } else {
      this.datePickerClosed.emit();
    }
  }

  @Watch('disableDates')
  splitDisabledDates() {
    if (this.disableDates) {
      this.disabledDatesArr = this.disableDates.split(',');
    }
  }

  @Watch('min')
  @Watch('max')
  transformDateFormatIfNecessary() {
    if (this.min?.match(/\d\d[.]\d\d[.]\d\d\d\d/)) {
      this.min = this.transformToDefaultDateFormat(this.min, 'dd.MM.yyyy');
    }
    if (this.max?.match(/\d\d[.]\d\d[.]\d\d\d\d/)) {
      this.max = this.transformToDefaultDateFormat(this.max, 'dd.MM.yyyy');
    }
  }

  @Watch('max')
  private getMaxTimestamp() {
    const dayMonthYear = this.max?.split('-');
    if (dayMonthYear) {
      this.maxTimestamp = new Date(
        Number(dayMonthYear[0]),
        Number(dayMonthYear[1]) - 1,
        Number(dayMonthYear[2])
      ).getTime();
    }
  }

  @Watch('min')
  private getMinTimestamp() {
    const dayMonthYear = this.min?.split('-');
    if (dayMonthYear) {
      this.minTimestamp = parse(this.min, 'yyyy-MM-dd', new Date()).getTime();
    }
  }

  @Watch('maxDaysBefore')
  private getMaxDaysBefore() {
    if (this.maxDaysBefore === 0 || this.maxDaysBefore) {
      const minDate = subDays(new Date(), this.maxDaysBefore);
      this.min = format(minDate, 'yyyy-MM-dd');
    }
  }

  @Watch('maxDaysAfter')
  private getMaxDaysAfter() {
    if (this.maxDaysAfter === 0 || this.maxDaysAfter) {
      let maxDate = new Date();
      let count = 0;

      while (count < this.maxDaysAfter) {
        maxDate = addDays(maxDate, 1);
        const isWeekend = maxDate.getDay() === 0 || maxDate.getDay() === 6;
        if (isWeekend) {
          continue;
        }
        count++;
      }
      this.max = format(maxDate, 'yyyy-MM-dd');
    }
  }

  /**
   * Core Datepicker logic
   */

  @Watch('month')
  @Watch('year')
  applyMonth() {
    this.getDaysForCalendar();
  }

  @Watch('selectedDay')
  setSelectedDay() {
    if (this.selectedDay) {
      this.setSelectedDateValue();
    } else {
      const date = getToday();
      this.year = date.getFullYear();
      this.month = date.getMonth();
    }

    this.getDaysForCalendar();
    const date = new Date(this.selectedDay);
    this.value = format(date, 'yyyy-MM-dd');
  }

  @Watch("dateRangeErrorMessage")
  @Watch("showError")
  @Watch('value')
  async validateValue() {    
    await this.handleCustomValidation(this.combineCustomValidators());
    await this.validate();
  }
  
  @Watch("dateRangeErrorMessage")
  async setCustomErrorMessage() {    
    // Update customErrorMessage whenever dateRangeErrorMessage changes
    this.customErrorMessage = this.dateRangeErrorMessage;
  }

  combineCustomValidators(): string {
    let feedback: string[] = [];
    let validateWeekendFeedback = this.validateWeekend();
    let validateMinMaxFeedback = this.validateMinMax();
    let validateDisableDatesFeedback = this.validateDisableDates();

    validateWeekendFeedback && feedback.push(validateWeekendFeedback);
    validateMinMaxFeedback && feedback.push(validateMinMaxFeedback);
    validateDisableDatesFeedback && feedback.push(validateDisableDatesFeedback);

    return feedback.join(' ');
  }

  /**
   * Sets the value of the input. Watch is needed if the format is changed dynamically
   */
  @Watch('dateFormat')
  setSelectedDateValue() {
    if (this.selectedDay) {
      const date = new Date(this.selectedDay);
      this.month = date.getMonth();
      this.year = date.getFullYear();
      this.selectedDateValue = format(date, 'yyyy-MM-dd'); // format is always yyyy-MM-dd which is the date default for web
      this.calendarInput.value = this.selectedDateValue;
    }
  }

  getRelevantYears() {
    let currentYear = new Date().getFullYear();
    let minYear = currentYear - 100;
    let maxYear = currentYear;
    let tempRelevantYear = [];

    if (this.min) minYear = new Date(this.min).getFullYear();
    if (this.max) maxYear = new Date(this.max).getFullYear();

    for (var i = minYear; i <= maxYear; i++) {
      tempRelevantYear.push(i);
    }

    this.relevantYears = tempRelevantYear;
  }

  /**
   * Gets the details of the day based on the selected month and year in the calendar
   * @param indexOfDay the index of the day inside the calendar body
   * @param numberOfDays the number of days of the month
   * @param firstDay the first day of the month
   * @param year year selected in calendar
   * @param month month selected in calendar
   */
  private getDayDetails(indexOfDay: number, numberOfDays: number, firstDay: number, year: number, month: number): Day {
    let date = indexOfDay - firstDay;
    let day = indexOfDay % 7;
    let prevMonth = month - 1;
    let prevYear = year;
    if (prevMonth < 0) {
      //if month was january, the prevMonth is december
      prevMonth = 11;
      prevYear--;
    }
    let nextMonth = month + 1;
    if (nextMonth > 11) {
      nextMonth = 0;
    }
    let prevMonthNumberOfDays = this.getNumberOfDays(prevYear, prevMonth);
    let _date = (date < 0 ? prevMonthNumberOfDays + date : date % numberOfDays) + 1;
    let monthOfDay = date < 0 ? prevMonth : date >= numberOfDays ? nextMonth : month; //get previous or next month

    let yearOfDay = year;
    if (month == 0 && monthOfDay == 11) {
      //if current month is january but the month of the day is december > last year
      yearOfDay--;
    }
    if (month == 11 && monthOfDay == 0) {
      //if the current month is december but the month of the day is january > next year
      yearOfDay++;
    }

    return new Day(_date, day, monthOfDay, yearOfDay);
  }

  /**
   * returns the number of days for the given month
   * example: if you set add 35 days to the beginning of April in 2020,
   *  getDate returns 5 because its the 5th of April, so substracting that number gives you the days of the month
   *  new Date(2020,3,35) equals 5
   * 35 - 5 equals 30, which is the correct number of days in April 2020
   * @param year year of the month
   * @param month month
   */
  private getNumberOfDays(year: number, month: number) {
    return 35 - new Date(year, month, 35).getDate();
  }

  private getDaysForCalendar(): void {
    let firstDay = new Date(this.year, this.month).getDay();
    let numberOfDays = this.getNumberOfDays(this.year, this.month);
    let days = new Array<Day>();
    let rows = 5; //rows fo the calendar
    let monthEndDates = [28, 29, 30, 31];
    if (numberOfDays == 31 && firstDay == 6) {
      rows = 6;
    }
    let cols = 7; //columns of the calendar
    let index = 1; //start week should be monday
    let rowAboveIndex = 0;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        days.push(this.getDayDetails(index, numberOfDays, firstDay, this.year, this.month));
        index++;
      }
    }

    this.calendarDays = days;

    if (monthEndDates.includes(numberOfDays) && firstDay == 0) {
      for (let row = 0; row < 1; row++) {
        for (let col = 0; col < cols; col++) {
          days.unshift(this.getDayDetails(rowAboveIndex, numberOfDays, 0, this.year, this.month));
          rowAboveIndex--;
        }
      }
    }
  }

  private isCurrentDay(day: Day) {
    return day.timestamp === this.todayTimeStamp;
  }

  /**
   * check if the day is the selected day of the calendar
   * @param day day to check
   */
  private isSelectedDay(day: Day) {
    return day.timestamp === this.selectedDay;
  }

  private isWeekend(day: Day) {
    return day.day === 6 || day.day === 0;
  }

  private isSunday(day: Day) {
    return day.day === 0;
  }

  private getMonthStr() {
    return getMonthName(this.month, this.currentLocalization.locale);
  }

  private isDisabled(day: Day): boolean {
    if (this.isWeekend(day) && !this.weekends) {
      return true;
    }
    if (this.isSunday(day) && !this.sunday) {
      return true;
    }
    if (!(day.timestamp >= this.minTimestamp && day.timestamp <= this.maxTimestamp)) {
      return true;
    }
    if (this.disabledDatesArr && this.disabledDatesArr.length >= 1) {
      for (const disabledDate of this.disabledDatesArr) {
        let testDayConverted = parse(disabledDate.trim(), 'yyyy-MM-dd', new Date())?.getTime();
        if (testDayConverted === day.timestamp) {
          return true;
        }
      }
    }
    return false;
  }

  private handleKeyDownMonthSelection(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }

    switch (e.key) {
      case KeyIds.UP:
        e.preventDefault();
        if (this.focussedMonth > 2) this.focussedMonth = this.focussedMonth - 3;
        break;
      case KeyIds.DOWN:
        e.preventDefault();
        if (this.focussedMonth < 9) this.focussedMonth = this.focussedMonth + 3;
        break;
      case KeyIds.LEFT:
        e.preventDefault();
        if (this.focussedMonth > 0 && this.focussedMonth % 3 > 0) {
          this.focussedMonth = this.focussedMonth - 1;
        }
        break;
      case KeyIds.RIGHT:
        e.preventDefault();
        if (this.focussedMonth < 11 && this.focussedMonth % 3 < 2) this.focussedMonth = this.focussedMonth + 1;
        break;
      case KeyIds.HOME:
        this.focussedMonth = 0;
        break;
      case KeyIds.END:
        this.focussedMonth = 11;
        break;
    }
  }

  private handleKeyDownYearSelection(e: KeyboardEvent, index: number) {
    if (this.disabled) {
      return;
    }

    const minYear = Number(this.min.substring(0, 4));
    const maxYear = Number(this.max.substring(0, 4));

    switch (e.key) {
      case KeyIds.UP:
        e.preventDefault();
        if (minYear <= this.focussedYear - 4) {
          this.focussedYear = this.focussedYear - 4;
        }
        break;
      case KeyIds.DOWN:
        e.preventDefault();
        if (maxYear >= this.focussedYear + 4) {
          this.focussedYear = this.focussedYear + 4;
        }
        break;
      case KeyIds.LEFT:
        e.preventDefault();
        if (minYear < this.focussedYear && index % 4 > 0) {
          this.focussedYear = this.focussedYear - 1;
        }
        break;
      case KeyIds.RIGHT:
        e.preventDefault();
        if (maxYear > this.focussedYear && index % 4 < 3) {
          this.focussedYear = this.focussedYear + 1;
        }
        break;
      case KeyIds.HOME:
        e.preventDefault();
        this.focussedYear = minYear;
        break;
      case KeyIds.END:
        e.preventDefault();
        this.focussedYear = maxYear;
        break;
    }
  }

  @Watch('focussedYear')
  focusYear() {
    setTimeout(() => {
      const selectedYearButton = this.yearSelectionContainer.querySelector('.eonui-focussed') as HTMLButtonElement;
      if (selectedYearButton) {
        selectedYearButton.focus();
      }
    }, 50);
  }

  @Watch('focussedMonth')
  focusMonth() {
    if (this.focussedMonth !== null) {
      setTimeout(() => {
        const selectedMonthButton = this.monthSelectionContainer.querySelector('.eonui-focussed') as HTMLButtonElement;
        if (selectedMonthButton) {
          selectedMonthButton.focus();
        } else {
          (this.monthSelectionContainer.querySelector('.eonui-today') as HTMLButtonElement).focus();
        }
      }, 50);
    }
  }

  @Watch('focussedDay')
  focusDay() {
    if (this.focussedDay) {
      setTimeout(() => {
        (
          this.calendarContainer.querySelector(
            '.eonui-date-picker-container-body-calendar .eonui-focussed'
          ) as HTMLButtonElement
        )?.focus();
      }, 50);
    }
  }

  private handleKeyDownInCalendar(e: KeyboardEvent) {
    if (this.disabled) {
      return;
    }

    switch (e.key) {
      case KeyIds.SPACE:
        e.preventDefault();
        (e.currentTarget as HTMLElement).click();
        break;
      case KeyIds.ENTER:
        e.stopPropagation();
        (e.currentTarget as HTMLElement).click();
        break;
      case KeyIds.UP:
        e.preventDefault();
        (this.handleKeyboardArrowNavigation(e.currentTarget, 'up') as HTMLElement).focus();
        break;
      case KeyIds.DOWN:
        e.preventDefault();
        (this.handleKeyboardArrowNavigation(e.currentTarget, 'down') as HTMLElement).focus();
        break;
      case KeyIds.LEFT:
        e.preventDefault();
        (this.handleKeyboardArrowNavigation(e.currentTarget, 'left') as HTMLElement).focus();
        break;
      case KeyIds.RIGHT:
        e.preventDefault();
        (this.handleKeyboardArrowNavigation(e.currentTarget, 'right') as HTMLElement).focus();
        break;
    }
  }

  /**
   * Keyboard arrow navigation
   * Returns new element to focus
   */
  handleKeyboardArrowNavigation(elm, direction) {
    const currentDate = (elm as HTMLElement).parentElement;
    const row = currentDate.parentElement;
    const daysArray = Array.from(row.children);
    const rowsArray = Array.from(row.parentElement.children);
    const dayIndex = daysArray.indexOf(currentDate);
    const rowIndex = rowsArray.indexOf(row);
    let newDayIndex;
    let newRowIndex;

    switch (direction) {
      case 'up':
        newDayIndex = dayIndex;
        newRowIndex = rowIndex > 1 ? rowIndex - 1 : 1;

        break;

      case 'down':
        newDayIndex = dayIndex;
        newRowIndex = rowIndex < rowsArray.length - 1 ? rowIndex + 1 : rowsArray.length - 1;

        break;

      case 'left':
        if (dayIndex === 0) {
          newRowIndex = rowIndex - 1;
          newDayIndex = daysArray.length - 1;
        } else {
          newDayIndex = dayIndex > 0 ? dayIndex - 1 : 0;
          newRowIndex = rowIndex;
        }

        break;

      case 'right':
        if (dayIndex === daysArray.length - 1) {
          newDayIndex = 0;
          newRowIndex = rowIndex + 1;
        } else {
          newDayIndex = dayIndex < daysArray.length - 1 ? dayIndex + 1 : daysArray.length - 1;
          newRowIndex = rowIndex;
        }

        break;

      default:
        newRowIndex = rowIndex;
        newDayIndex = dayIndex;
    }

    return rowsArray[newRowIndex].children[newDayIndex].children[0];
  }

  /**
   * fired when a user clicks the day in the calendar
   * @param day day to select
   */
  onDateClick(day: Day) {
    if (this.isDisabled(day)) return;

    this.selectedDay = day.timestamp;
    this.setSelectedDay(); // Watch is not called when interne selectedDay is the same

    if (day.month != this.month) {
      this.month = day.month;
      this.year = day.year;
      this.getDaysForCalendar();
    }

    if (this.closeOnSelect) {
      this.closeCalendar(true);
    }

    this.calendarInput.focus();
  }

  /**
   * Change the selected month according to the offset
   * @param offset offset for the month
   */
  setMonth(offset: number) {
    let year = this.year;
    let month = this.month + offset;
    if (month === -1) {
      month = 11;
      year--;
    } else if (month === 12) {
      month = 0;
      year++;
    }

    this.year = year;
    this.month = month;
    this.getDaysForCalendar();
  }

  /**
   * renders the calendar
   */
  private renderCalendar() {
    const rowArrays = this.calendarDays.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / 7);

      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = []; // start a new chunk
      }

      resultArray[chunkIndex].push(item);

      return resultArray;
    }, []);

    const rows = rowArrays.map((row) => {
      return (
        <tr class={pfx('date-picker-container-body-calendar-body-row')}>
          {row.map((day, index) => {
            return (
              <td
                class={pfx({
                  'date-picker-container-body-calendar-body-day': true,
                  today: this.isCurrentDay(day),
                })}
                key={index}
                aria-selected={this.isSelectedDay(day) && 'true'}
              >
                <button
                  aria-label={new Date(day.timestamp).toLocaleDateString(this.currentLocalization.locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                  aria-current={this.isCurrentDay(day) && 'date'}
                  class={pfx({
                    'date-picker-container-body-calendar-body-day-text': true,
                    selected: this.isSelectedDay(day),
                    focussed: this.focussedDay === day.date && day.month === this.month,
                  })}
                  onClick={() => this.onDateClick(day)}
                  onKeyDown={(event) => this.handleKeyDownInCalendar(event)}
                  onFocus={() => (this.focussedDay = day.date)}
                  onBlur={() => (this.focussedDay = null)}
                  disabled={day.month !== this.month || this.isDisabled(day)}
                >
                  {day.date}
                </button>
              </td>
            );
          })}
        </tr>
      );
    });

    return (
      <table class={pfx('date-picker-container-body-calendar')} role={'grid'}>
        <tr class={pfx('date-picker-container-body-calendar-head')}>
          {getWeekDays(this.currentLocalization.locale).map((day, i) => (
            <th key={i} class={pfx('date-picker-container-body-calendar-head-name')} aria-label={day.fullName}>
              {day.shortName}
            </th>
          ))}
        </tr>
        {rows}
      </table>
    );
  }

  toggleMonthYearSelection(type) {
    if (type === this.monthYearSelection) {
      this.monthYearSelection = '';
      return;
    }

    if (type === 'month' && this.monthYearSelection !== 'month') {
      this.monthYearSelection = 'month';
      this.focussedMonth = this.month;
    }

    if (type === 'year' && this.monthYearSelection !== 'year') {
      this.monthYearSelection = 'year';
      this.focussedYear = this.year;

      setTimeout(() => {
        this.yearSelectionContainer.scrollTop = this.selectedDate
          ? (this.yearSelectionContainer.querySelector('.eonui-selected') as HTMLElement)?.offsetTop - 2
          : (this.yearSelectionContainer.querySelector('.eonui-today') as HTMLElement)?.offsetTop - 2;
        this.focusYear();
      }, 0);
    }
  }

  selectMonth(month) {
    this.month = month;
    this.focussedMonth = null;
    this.focussedYear = this.value ? Number(this.value.slice(0, 4)) : this.year;
    this.monthYearSelection = 'year';
    this.focusYear();
  }

  selectYear(year) {
    this.year = year;
    this.monthYearSelection = null;
    if (!this.focussedMonth) this.focussedMonth = this.month;
    this.focussedDay = this.value ? Number(this.value.slice(-2)) : 1;
  }

  private renderMonthYearSelection() {
    const monthIndices = Array.from({ length: 12 }, (_, i) => i + 1);
    const today = getToday();

    return (
      <Fragment>
        <div
          class={pfx({ 'month-selection-container': true, active: this.monthYearSelection === 'month' })}
          ref={(el) => (this.monthSelectionContainer = el as HTMLInputElement)}
        >
          {monthIndices.map((month) => {
            return (
              <button
                class={pfx({
                  'month-year-button': true,
                  selected: this.value
                    ? month - 1 === this.month
                    : this.focussedDay && month - 1 === this.focussedMonth,
                  focussed: month - 1 === this.focussedMonth,
                  today: today.getMonth() === month - 1 && today.getFullYear() === this.year,
                })}
                aria-label={format(new Date(2000, month - 1, 1), 'LLLL', {
                  locale: this.currentLocalization.dateFnsLocale,
                })}
                onClick={() => this.selectMonth(month - 1)}
                onFocus={() => (this.focussedMonth = month - 1)}
                onBlur={() => (this.focussedMonth = null)}
                onKeyDown={(event: KeyboardEvent) => this.handleKeyDownMonthSelection(event)}
              >
                {format(new Date(2000, month - 1, 1), 'LLL', { locale: this.currentLocalization.dateFnsLocale })}
              </button>
            );
          })}
        </div>
        <div
          class={pfx({ 'year-selection-container': true, active: this.monthYearSelection === 'year' })}
          ref={(el) => (this.yearSelectionContainer = el as HTMLInputElement)}
        >
          {this.relevantYears.map((year, index) => {
            return (
              <button
                class={pfx({
                  'month-year-button': true,
                  selected: this.value
                    ? this.value && year === this.year
                    : this.focussedMonth && year === this.focussedYear,
                  focussed: year === this.focussedYear,
                  today: today.getFullYear() === year,
                })}
                onClick={() => this.selectYear(year)}
                onFocus={() => (this.focussedYear = year)}
                onBlur={() => (this.focussedYear = null)}
                onKeyDown={(event: KeyboardEvent) => this.handleKeyDownYearSelection(event, index)}
              >
                {year}
              </button>
            );
          })}
        </div>
      </Fragment>
    );
  }

  private onInputKeyDown(event: KeyboardEvent) {
    switch (event.key) {
      case KeyIds.SPACE:
        if (this.datePickerVisible === false) {
          if (this.calendarInput.shadowRoot.activeElement.classList.contains('eonui-clear')) {
            this.closeCalendar(true);
          } else {
            event.preventDefault();
            this.openCalendar();
          }
        }
        break;
      case KeyIds.DOWN:
        if (event.altKey) this.openCalendar();
        break;
    }
  }

  @Watch('selectedDatePart')
  selectDatePart() {
    switch (this.selectedDatePart) {
      case 'd':
        const dayPosition = this.dateFormat.toLowerCase().indexOf('d');
        this.calendarInputElement.setSelectionRange(dayPosition, dayPosition + 2);
        break;
      case 'm':
        const monthPosition = this.dateFormat.toLowerCase().indexOf('m');
        this.calendarInputElement.setSelectionRange(monthPosition, monthPosition + 2);
        break;
      case 'y':
        const yearPosition = this.dateFormat.toLowerCase().indexOf('y');
        this.calendarInputElement.setSelectionRange(yearPosition, yearPosition + 4);
        break;
    }
  }

  //ENVIAM
  defaultValue: string;
  valueMissing = false;

  private updateInternalState() {
    this.updateInternalValue();
    this.updateInternalValidity();
    this.validity = this.internals.validity;
    this.validationMessage = this.internals.validationMessage;
    this.defaultValue = this.value;
  }

  @Watch('value')
  private updateInternalValue() {
    this.internals.setFormValue(this.value);
  }

  private updateInternalValidity() {
    const validity = this.getValidity();
    const errorMessage = this.customErrorMessage || this.customRequiredMessage;
    this.internals.setValidity(validity, errorMessage, this.calendarInput);
  }

  private getValidity(): ValidityState {
    const validity = {
      valid: !this.invalid,
      valueMissing: this.valueMissing,
    } as ValidityState;
    return validity;
  }

  formDisabledCallback(disabled: boolean) {
    if (this.disabled !== disabled) {
      this.disabled = disabled;
    }
  }

  formResetCallback() {
    this.internals.setValidity({});
    this.internals.setFormValue(this.defaultValue);
  }

  formStateRestoreCallback(state, _mode) {
    this.value = state;
  }

  @Method()
  async checkValidity() {
    return this.internals.checkValidity();
  }

  @Method()
  async reportValidity() {
    return this.internals.reportValidity();
  }

  @Method()
  async setCustomValidity(errorMessage: string) {
    this.errorMessage = errorMessage;
    await this.handleCustomValidation(errorMessage);
  }

  @Prop()
  validity: ValidityState;

  @Prop()
  validationMessage: string;

  render() {
    const hidePopupContainer = this.hidePopup;
    const infoIconWrapper = () => {
      return (
        <div class={pfx('info-icon-wrapper')}>
          <slot name="info-icon" slot="info-icon" />
        </div>
      );
    };

    return (
      <Host>
        <div
          class={pfx({
            'date-picker-wrapper': true,
            [this.size]: this.size,
            'label-outside': this.labelOutside,
            focus: this.datePickerVisible,
            'auto-width': this.autoInputWidth,
          })}
        >
          <envc-eon-ui-input
            type="text"
            ref={(el) => (this.calendarInput = el as HTMLEnvcEonUiInputElement)}
            value={this.selectedDateValue}
            disabled={this.disabled}            
            invalid={this.invalid}
            show-error={this.showError}
            error-message={this.errorMessage}
            custom-error-message={this.customErrorMessage ?? this.dateRangeErrorMessage}
            custom-required-message={this.customRequiredMessage}
            label={this.label}
            label-outside={this.labelOutside}
            success={this.success}
            success-message={this.successMessage}
            info={this.info}
            info-message={this.infoMessage}
            trailing-icon-name="calendar"
            trailing-icon-label={
              this.datePickerVisible ? this.currentLocalization.closeCalendar : this.currentLocalization.openCalendar
            }
            trailing-icon={true}
            focus-style={this.datePickerVisible}
            clear-input-icon={this.clearInputIcon}
            date-mask={true}
            date-format={this.dateFormat}
            date-max={this.max}
            date-min={this.min}
            date-mask-allow-weekends={this.weekends}
            onIconClick={(e) => this.toggleOpen(e)}
            onKeyDown={(e) => this.onInputKeyDown(e)}
            scheme={this.activeScheme.internalInputScheme}
            required={this.required}
            encapsulated={true}
            size={this.size}
            is-date-picker={true}
            highlighted-necessity={this.highlightedNecessity}
            a11yAriaLabel={this.a11yAriaLabel}
          >
            {this.isInfoIconAvailable && infoIconWrapper()}
          </envc-eon-ui-input>
          {!hidePopupContainer && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label={this.currentLocalization.chooseDate}
              class={pfx({
                [`date-picker-container ${this.datePickerVisible && 'open'}`]: true,
                open: this.datePickerVisible,
                ['position-right']: this.datePickerContainerPositionRight,
              })}
              ref={(el) => (this.calendarContainer = el as HTMLInputElement)}
            >
              <div class={pfx({ 'month-year-selection-container': true })}>
                <div class={pfx({ 'month-year-selection-head': true })}>
                  <button
                    class={pfx({ 'month-year-toggle': true, month: true, active: this.monthYearSelection === 'month' })}
                    onClick={() => this.toggleMonthYearSelection('month')}
                    aria-expanded={this.monthYearSelection === 'month' ? 'true' : 'false'}
                  >
                    {this.getMonthStr()}
                    <span>
                      <envc-eon-ui-icon name="chevron_small_down"></envc-eon-ui-icon>
                    </span>
                  </button>
                  <button
                    class={pfx({ 'month-year-toggle': true, year: true, active: this.monthYearSelection === 'year' })}
                    onClick={() => this.toggleMonthYearSelection('year')}
                    aria-expanded={this.monthYearSelection === 'year' ? 'true' : 'false'}
                  >
                    {this.year}
                    <span>
                      <envc-eon-ui-icon name="chevron_small_down"></envc-eon-ui-icon>
                    </span>
                  </button>
                </div>
                <div class={pfx({ 'month-year-selection-body': true, active: this.monthYearSelection })}>
                  {this.renderMonthYearSelection()}
                </div>
              </div>
              <div class={pfx('date-picker-container-body')}>
                <div class={pfx('date-picker-container-footer')}>
                  <button
                    class={pfx({ 'date-picker-nav-button': true, previous: true })}
                    onClick={() => this.setMonth(-1)}
                    aria-label={this.currentLocalization.prevMonth}
                  >
                    <envc-eon-ui-icon
                      aria-label="chevron_small_left"
                      name="chevron_small_left"
                      size="normal"
                      scheme={ICON_ICON_SCHEME}
                    ></envc-eon-ui-icon>
                  </button>
                  <span class={pfx('date-picker-current-month-year')}>
                    {this.getMonthStr()} {this.year}
                  </span>
                  <button
                    class={pfx({ 'date-picker-nav-button': true, next: true })}
                    onClick={() => this.setMonth(1)}
                    aria-label={this.currentLocalization.nextMonth}
                  >
                    <envc-eon-ui-icon
                      aria-label="right"
                      name="chevron_small_right"
                      size="normal"
                      scheme={ICON_ICON_SCHEME}
                    ></envc-eon-ui-icon>
                  </button>
                </div>
                {this.renderCalendar()}
              </div>
            </div>
          )}
        </div>
      </Host>
    );
  }
}
